import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { InfoCard } from '@backstage/core-components';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { useEnv } from './EnvContext';
import { EntitySparrowDeskSelectorBar } from './EntitySparrowDeskSelectorBar';
import { findApp, urlsForApp } from '../coalitions';

// Env-scoped Grafana Explore iframe. Loki query template from the
// coalition drives which container / namespace selector gets used.
export const EntitySparrowDeskLogsIframe = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { coalition, env } = useEnv(entity, config);

  const app = coalition ? findApp(coalition, entity) : undefined;

  if (!coalition || !app || !env) {
    return (
      <InfoCard title="Logs">
        <Typography color="textSecondary">
          Logs iframe isn't configured for this entity.
        </Typography>
      </InfoCard>
    );
  }

  const urls = urlsForApp(app, env, coalition);
  if (!urls || !urls.logs) {
    // Keep the env selector so the user can switch to an env that has logs.
    return (
      <>
        <Box mb={2}>
          <EntitySparrowDeskSelectorBar />
        </Box>
        <InfoCard title={`Logs — ${env} / ${app.name}`}>
          <Typography color="textSecondary">
            No logs link for {app.name} in {env}.
          </Typography>
        </InfoCard>
      </>
    );
  }

  // The LogQL query is baked into the Explore URL, but we surface it
  // separately in a caption so devs can see the filter without opening
  // the query editor.
  const paneMatch = urls.logs.match(/panes=([^&]+)/);
  let query = '';
  if (paneMatch) {
    try {
      const decoded = JSON.parse(decodeURIComponent(paneMatch[1]));
      query = decoded?.gnt?.queries?.[0]?.expr ?? '';
    } catch {
      /* ignore */
    }
  }

  return (
    <>
      <Box mb={2}>
        <EntitySparrowDeskSelectorBar />
      </Box>
      <InfoCard title={`Logs — ${env} / ${app.name}`} noPadding>
        {query && (
          <Box px={2} py={1} bgcolor="background.default">
            <Typography variant="caption" color="textSecondary">
              LogQL: <code>{query}</code>
            </Typography>
          </Box>
        )}
        <iframe
          src={urls.logs}
          title={`Grafana Explore — ${query || app.name}`}
          style={{ width: '100%', height: '80vh', border: 0 }}
        />
      </InfoCard>
    </>
  );
};

export default EntitySparrowDeskLogsIframe;
